import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RouterModule, Router } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { map, catchError } from 'rxjs/operators';

import { CampanhaService } from '@app/shared/components/api/services/campanha.service';
import { CurtidaService } from '@app/shared/components/api/services/curtida.service';
import { ComentarioService } from '@app/shared/components/api/services/comentario.service';
import { CategoriaService } from '@app/shared/components/api/services/categoria.service';
import { ProvinciaService } from '@app/shared/components/api/services/provincia.service';
import { EnumService } from '@app/shared/components/api/services/enum.service';
import { DashboardService } from '@app/shared/components/api/services/dashboard.service';
import { UserService } from '@app/auth/services/user.service';
import { AuthService } from '@app/auth/services/auth.service';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { MoneyPipe } from '@app/shared/pipes/money.pipe';
import { DateMonthPipe } from '@app/shared/pipes/date-month.pipe';
import { Select, SelectModule } from 'primeng/select';

@Component({
  selector: 'app-home-campanha',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MoneyPipe,
    DateMonthPipe,
    SelectModule,
  ],
  providers: [
    CampanhaService,
    CategoriaService,
    ProvinciaService,
    EnumService,
    DashboardService,
    UserService,
  ],
  templateUrl: './home-campanha.component.html',
  styleUrl: './home-campanha.component.scss',
})
export class HomeCampanhaComponent implements OnInit, OnDestroy {
  private readonly campanhaService = inject(CampanhaService);
  private readonly curtidaService = inject(CurtidaService);
  private readonly comentarioService = inject(ComentarioService);
  private readonly categoriaService = inject(CategoriaService);
  private readonly provinciaService = inject(ProvinciaService);
  private readonly enumService = inject(EnumService);
  private readonly dashboardService = inject(DashboardService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);

  @ViewChild('commentInput') commentInputRef!: ElementRef<HTMLTextAreaElement>;

  // Data
  campanhas: any[] = [];
  campanhasSucesso: any[] = [];
  categoriaList: any[] = [];
  provinciaList: any[] = [];
  pagination: Pagination<any> = new Pagination<any>();
  loading = false;
  loadingSucesso = false;

  // View state
  viewMode: 'grid' | 'list' = 'grid';

  // Filters
  selectedCategory: number | null = null;
  selectedProvincia: number | null = null;
  selectedTipoFinanciamento: number | null = null;
  selectedSort: string = 'recent';
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private searchSub?: Subscription;
  mostrarEncerradas = false;
  newsletterEmail = '';

  tipoFinanciamentoList: { id: number; label: string }[] = [
    { id: 1, label: 'Doação' },
    { id: 2, label: 'Empréstimo' },
    { id: 3, label: 'Recompensa' },
  ];

  sortOptions = [
    { label: 'Mais Recentes', value: 'recent' },
    { label: 'Maior % da Meta', value: 'progress' },
    { label: 'Mais Apoiadores', value: 'supporters' },
    { label: 'A Terminar Primeiro', value: 'ending' },
    { label: 'Maior Valor Arrecadado', value: 'amount' },
  ];

  // Stats
  stats = {
    totalArrecadado: 0,
    taxaSucesso: 73,
    apoiadoresActivos: 0,
    provinciasCount: 18,
  };

  // Toast
  toastMessage = '';
  toastVisible = false;
  private toastTimeout: any;

  // Social interactions
  likedSet = new Set<number>();
  unlikedSet = new Set<number>();
  savedSet = new Set<number>();

  // Comment modal
  showCommentModal = false;
  activeCommentCamp: any = null;
  commentContent = '';
  isSubmittingComment = false;
  userInitials: string = '';

  userID: string | null = this.userService.getUserId();

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.showCommentModal) {
      this.closeCommentModal();
    }
  }

  ngOnInit(): void {
    this.pagination.pageSize = 12;
    this.pagination.pageNumber = 1;
    this.userInitials = this.userService.getUserInitials() || 'U';
    this.loadSavedFromLocalStorage();
    this.initAuthAndLikesSync();
    this.loadDropdowns();
    this.loadStats();
    this.getAllCampanhas();
    this.getCampanhasSucesso();
    this.searchSub = this.searchSubject.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
      this.pagination.pageNumber = 1;
      this.getAllCampanhas();
    });
  }

  initAuthAndLikesSync(): void {
    this.userID = this.userService.getUserId();
    this.userInitials = this.userService.getUserInitials() || 'U';
    this.authService.isAuthenticatedChange().subscribe((isAuth) => {
      this.userID = this.userService.getUserId();
      this.userInitials = this.userService.getUserInitials() || 'U';
      if (isAuth && this.userID) {
        this.curtidaService.loadUserLikes(this.userID).subscribe(() => {
          this.sincronizarCurtidas();
        });
      } else if (!isAuth) {
        this.likedSet.clear();
        this.unlikedSet.clear();
        this.sincronizarCurtidas();
      }
    });

    this.curtidaService.userLikes$.subscribe(() => {
      this.sincronizarCurtidas();
    });

    if (this.userID) {
      this.curtidaService.loadUserLikes(this.userID).subscribe(() => {
        this.sincronizarCurtidas();
      });
    }
  }

  sincronizarCurtidas(): void {
    const processCamp = (camp: any) => {
      if (!camp) return;

      if (
        camp.curtidas?.totalCurtidas !== undefined &&
        camp.curtidas?.totalCurtidas !== null
      ) {
        camp.totalCurtidas = Number(camp.curtidas.totalCurtidas);
      } else if (
        camp.totalCurtidas === undefined ||
        camp.totalCurtidas === null
      ) {
        camp.totalCurtidas = 0;
      }

      const campId = Number(camp.id);
      const isLikedInBackend = Boolean(camp.curtidas?.utilizadorCurtiu);
      const isLikedInCache = this.curtidaService.isCampanhaLiked(campId);
      const isExplicitlyUnliked = this.unlikedSet.has(campId);

      const isLiked = this.userID
        ? isLikedInCache || (isLikedInBackend && !isExplicitlyUnliked)
        : false;

      const cId =
        camp.curtidas?.curtidasId ?? this.curtidaService.getCurtidaId(campId);
      if (cId && isLiked) {
        camp.curtidaID = Number(cId);
        this.curtidaService.recordLike(campId, Number(cId));
      }

      if (isLiked) {
        this.likedSet.add(campId);
        if (camp.curtidas) {
          camp.curtidas.utilizadorCurtiu = true;
        }
      } else {
        this.likedSet.delete(campId);
        camp.curtidaID = null;
        if (camp.curtidas) {
          camp.curtidas.curtidasId = null;
          camp.curtidas.utilizadorCurtiu = false;
        }
      }
    };

    (this.campanhas || []).forEach(processCamp);
    (this.campanhasSucesso || []).forEach(processCamp);
  }

  loadDropdowns() {
    forkJoin({
      categorias: this.categoriaService.listPublic({
        pageSize: 100,
        CategoriaEmUso: true,
      }),
      provincias: this.provinciaService.list({ pageSize: 100, ProvinciaEmUso: true }),
      tiposFinanciamento: this.enumService
        .getAllTipoFinanciamento()
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: (res) => {
        if (res.categorias?.data) {
          this.categoriaList = res.categorias.data;
        }
        if (res.provincias?.data) {
          this.provinciaList = res.provincias.data;
        }
        if (res.tiposFinanciamento?.data) {
          const raw = res.tiposFinanciamento.data;
          if (Array.isArray(raw)) {
            this.tipoFinanciamentoList = raw.map((item: any) => ({
              id: Number(item.id ?? item.valor ?? item.key),
              label: item.label ?? item.descricao ?? item.nome ?? String(item),
            }));
          } else if (typeof raw === 'object') {
            this.tipoFinanciamentoList = Object.entries(raw).map(
              ([key, value]) => ({
                id: Number(key),
                label: value as string,
              }),
            );
          }
        }
      },
      error: (err) =>
        console.error('Erro ao carregar categorias/províncias/tipos', err),
    });
  }

  loadStats(): void {
    forkJoin({
      statsPublicas: this.dashboardService
        .getEstatisticasPublicas()
        .pipe(catchError(() => of(null))),
      provincias: this.provinciaService
        .list({ pageSize: 100 })
        .pipe(catchError(() => of({ data: [] }))),
    }).subscribe({
      next: (res: any) => {
        const stats = res.statsPublicas || {};
        const provData = res.provincias;
        const totalProvincias =
          provData?.totalRecords ??
          provData?.total ??
          (Array.isArray(provData?.data) && provData.data.length > 0
            ? provData.data.length
            : 18);

        this.stats = {
          totalArrecadado: stats.totalFinanciado ?? stats.pagamentosConfirmados ?? 0,
          taxaSucesso: stats.taxaSucesso ?? 0,
          apoiadoresActivos: stats.totalUtilizadoresAtivos ?? 0,
          provinciasCount: totalProvincias,
        };
      },
      error: (err) => {
        console.error('Erro ao carregar estatísticas:', err);
      },
    });
  }

  getAllCampanhas() {
    this.loading = true;
    const params: any = {
      PageSize: this.pagination.pageSize || 8,
      PageNumber: this.pagination.pageNumber || 1,
      UtilizadorCurtiuID: this.userID || '',
      Randomizar: false,
      EstadoCampanhaENUM: this.mostrarEncerradas ? 8 : 4,
    };

    if (this.selectedCategory) {
      params.CategoriaId = this.selectedCategory;
    }

    if (this.selectedProvincia) {
      params.ProvinciaID = this.selectedProvincia;
    }

    if (this.selectedTipoFinanciamento) {
      params.TipoFinanciamentoENUM = this.selectedTipoFinanciamento;
    }

    if (this.searchQuery && this.searchQuery.trim()) {
      const termo = this.searchQuery.trim();
      params.Pesquisa = termo;
      params.Titulo = termo;
    }

    // Mapping sort
    switch (this.selectedSort) {
      case 'progress':
        params.OrderBy = 'valorArrecado desc';
        break;
      case 'supporters':
        params.OrderBy = 'totalDoadores desc';
        break;
      case 'ending':
        params.OrderBy = 'dataTermino asc';
        break;
      case 'amount':
        params.OrderBy = 'metaArrecadar desc';
        break;
      case 'recent':
      default:
        params.OrderBy = 'id desc';
        break;
    }

    this.campanhaService.listPublic(params).subscribe({
      next: (res) => {
        this.pagination = new Pagination<any>().deserialize(res);
        let data = this.pagination?.data ?? res?.data ?? [];
        if (this.searchQuery && this.searchQuery.trim()) {
          const q = this.searchQuery.trim().toLowerCase();
          data = data.filter((c: any) => {
            const tit = (c.titulo || '').toLowerCase();
            const desc = (c.descricao || '').toLowerCase();
            const descCurta = (c.descricaoCurta || '').toLowerCase();
            return tit.includes(q) || desc.includes(q) || descCurta.includes(q);
          });
        }
        this.campanhas = data;
        this.sincronizarCurtidas();

        // Load images
        this.loadCampaignPhotos(this.campanhas);
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao listar campanhas', err);
        this.loading = false;
      },
    });
  }

  getCampanhasSucesso() {
    this.loadingSucesso = true;
    const params = {
      PageSize: 4,
      PageNumber: 1,
      CampanhasBemSucedidas: true,
      Randomizar: true,
    };

    this.campanhaService.listPublic(params).subscribe({
      next: (res) => {
        const data = res?.data ?? [];
        this.campanhasSucesso = data;
        this.sincronizarCurtidas();
        this.loadingSucesso = false;
      },
      error: (err) => {
        console.error('Erro ao listar campanhas de sucesso', err);
        this.loadingSucesso = false;
      },
    });
  }

  loadCampaignPhotos(camps: any[]) {
    if (!camps || camps.length === 0) return;

    const photoRequests = camps.map((camp: any) => {
      const anexos = camp.anexoCampanha ?? [];
      const imagem = anexos.find(
        (a: any) =>
          (a?.tipoAnexoCampanha?.tipo || '').toString().toLowerCase() ===
          'imagem',
      );

      if (imagem?.anexo?.url) {
        camp.fotoUrl = imagem.anexo.url;
        return of(null);
      }

      if (imagem?.anexo?.id) {
        return this.campanhaService.getPhotoCampanhaByID(imagem.anexo.id).pipe(
          map((fotoRes: any) => {
            camp.fotoUrl =
              typeof fotoRes === 'string'
                ? fotoRes
                : this.sanitizer.bypassSecurityTrustUrl(
                    URL.createObjectURL(fotoRes),
                  );
          }),
          catchError(() => {
            camp.fotoUrl = null;
            return of(null);
          }),
        );
      }
      camp.fotoUrl = null;
      return of(null);
    });

    forkJoin(photoRequests).subscribe();
  }

  setView(mode: 'grid' | 'list') {
    this.viewMode = mode;
  }

  filterCat(catId: number | null) {
    this.selectedCategory = catId;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  filterProvincia(provId: number | null) {
    this.selectedProvincia = this.selectedProvincia === provId ? null : provId;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  clearProvinces() {
    this.selectedProvincia = null;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  onSortChange(val: string) {
    this.selectedSort = val;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  onTipoFinanciamentoChange(val: any) {
    this.selectedTipoFinanciamento =
      val !== null && val !== undefined && val !== '' ? Number(val) : null;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  clearAllFilters(): void {
    this.searchQuery = '';
    this.selectedCategory = null;
    this.selectedProvincia = null;
    this.selectedTipoFinanciamento = null;
    this.selectedSort = 'recent';
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  getTipoFinanciamentoLabel(tipo: any): string {
    if (!tipo) return 'Doação';
    if (typeof tipo === 'string') return tipo;
    const match = this.tipoFinanciamentoList.find((t) => t.id === Number(tipo));
    if (match) return match.label;
    switch (Number(tipo)) {
      case 1:
        return 'Doação';
      case 2:
        return 'Empréstimo';
      case 3:
        return 'Recompensa';
      default:
        return 'Doação';
    }
  }

  getTipoFinanciamentoIcon(tipo: any): string {
    const val = typeof tipo === 'string' ? tipo.toLowerCase() : Number(tipo);
    if (val === 2 || val === 'empréstimo' || val === 'emprestimo') {
      return 'fas fa-hand-holding-dollar';
    }
    if (val === 3 || val === 'recompensa') {
      return 'fas fa-gift';
    }
    return 'fas fa-hand-holding-heart';
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  onSearch() {
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  toggleEncerradas() {
    this.mostrarEncerradas = !this.mostrarEncerradas;
    this.pagination.pageNumber = 1;
    this.getAllCampanhas();
  }

  onPageChange(page: number) {
    if (
      page < 1 ||
      (this.pagination.totalPages && page > this.pagination.totalPages)
    )
      return;
    this.pagination.pageNumber = page;
    this.getAllCampanhas();
    window.scrollTo({ top: 300, behavior: 'smooth' });
  }

  getPageNumbers(): (number | string)[] {
    const total =
      this.pagination.totalPages ||
      Math.ceil(
        (this.pagination.totalRecords || 1) / (this.pagination.pageSize || 8),
      ) ||
      1;
    const current = this.pagination.pageNumber || 1;
    const pages: (number | string)[] = [];

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push('...');
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (current < total - 2) pages.push('...');
      if (!pages.includes(total)) pages.push(total);
    }
    return pages;
  }

  getPercent(camp: any): number {
    if (!camp?.metaArrecadar || camp.metaArrecadar <= 0) return 0;
    const pct = Math.round(
      ((camp.valorArrecado || 0) / camp.metaArrecadar) * 100,
    );
    return pct;
  }

  getDiasRestantes(dataTermino: string): string {
    if (!dataTermino) return 'Em curso';
    const termino = new Date(dataTermino).getTime();
    const agora = new Date().getTime();
    const diff = termino - agora;
    if (diff <= 0) return 'Concluída';
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return `${dias} dias`;
  }

  isUrgent(dataTermino: string): boolean {
    if (!dataTermino) return false;
    const diff = new Date(dataTermino).getTime() - new Date().getTime();
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return dias > 0 && dias <= 10;
  }

  isWarning(dataTermino: string): boolean {
    if (!dataTermino) return false;
    const diff = new Date(dataTermino).getTime() - new Date().getTime();
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return dias > 10 && dias <= 30;
  }

  getCategoryIcon(descricao?: string): string {
    const d = (descricao || '').toLowerCase();
    if (d.includes('aliment')) return 'fas fa-utensils';
    if (d.includes('saúde') || d.includes('saude')) return 'fas fa-heartbeat';
    if (d.includes('educa')) return 'fas fa-graduation-cap';
    if (d.includes('tecno')) return 'fas fa-laptop-code';
    if (d.includes('agri')) return 'fas fa-seedling';
    if (d.includes('arte') || d.includes('cultur')) return 'fas fa-palette';
    if (
      d.includes('negócio') ||
      d.includes('negocio') ||
      d.includes('empreend')
    )
      return 'fas fa-briefcase';
    if (d.includes('social') || d.includes('comun'))
      return 'fas fa-hands-helping';
    if (d.includes('ambient') || d.includes('ecolog')) return 'fas fa-leaf';
    if (d.includes('desport') || d.includes('esport')) return 'fas fa-running';
    return 'fas fa-shapes';
  }

  getPlaceholderGradient(descricao?: string): string {
    const d = (descricao || '').toLowerCase();
    if (d.includes('aliment')) return 'linear-gradient(135deg,#bbf7d0,#86efac)';
    if (d.includes('saúde') || d.includes('saude'))
      return 'linear-gradient(135deg,#fce7f3,#fbcfe8)';
    if (d.includes('educa')) return 'linear-gradient(135deg,#dbeafe,#bfdbfe)';
    if (d.includes('tecno')) return 'linear-gradient(135deg,#ede9fe,#ddd6fe)';
    if (d.includes('agri')) return 'linear-gradient(135deg,#fef3c7,#fde68a)';
    if (d.includes('arte') || d.includes('cultur'))
      return 'linear-gradient(135deg,#ffedd5,#fed7aa)';
    return 'linear-gradient(135deg,#e2e8f0,#cbd5e1)';
  }

  loadSavedFromLocalStorage(): void {
    try {
      const saved = JSON.parse(
        localStorage.getItem('fundcube_saved_campaigns') || '[]',
      );
      if (Array.isArray(saved)) {
        this.savedSet = new Set<number>(saved.map((id: any) => Number(id)));
      }
    } catch {
      this.savedSet = new Set<number>();
    }
  }

  toggleLike(camp: any) {
    if (!this.userID) {
      this.showToast('Faça login para curtir campanhas.');
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    const id = Number(camp.id);
    const isLiked =
      this.likedSet.has(id) || this.curtidaService.isCampanhaLiked(id);

    if (isLiked) {
      // 1. Atualizar estado local imediatamente
      this.likedSet.delete(id);
      this.unlikedSet.add(id);

      camp.totalCurtidas = Math.max(0, (camp.totalCurtidas || 1) - 1);
      const curtidaId =
        camp.curtidaID ||
        camp.curtidas?.curtidasId ||
        this.curtidaService.getCurtidaId(id);

      camp.curtidaID = null;
      if (camp.curtidas) {
        camp.curtidas.curtidasId = null;
        camp.curtidas.totalCurtidas = camp.totalCurtidas;
        camp.curtidas.utilizadorCurtiu = false;
      }

      // 2. Remover do cache de curtidas
      this.curtidaService.removeLike(id);

      // 3. Executar remoção no backend
      if (curtidaId) {
        this.curtidaService.delete(curtidaId).subscribe({
          error: (err) => console.error('Erro ao remover curtida:', err),
        });
      } else if (this.userID) {
        this.curtidaService.loadUserLikes(this.userID).subscribe((likes) => {
          const foundId = likes.get(id);
          if (foundId) {
            this.curtidaService.delete(foundId).subscribe({
              error: (err) => console.error('Erro ao remover curtida:', err),
            });
          }
        });
      }
      this.showToast('Gosto removido');
    } else {
      this.unlikedSet.delete(id);
      this.likedSet.add(id);
      camp.totalCurtidas = (camp.totalCurtidas || 0) + 1;
      if (camp.curtidas) {
        camp.curtidas.totalCurtidas = camp.totalCurtidas;
        camp.curtidas.utilizadorCurtiu = true;
      }

      this.curtidaService
        .store({
          id: 0,
          campanhaID: id,
          utilizadorID: this.userID,
        })
        .subscribe({
          next: (res: any) => {
            const createdId = res?.data?.id ?? res?.data;
            if (createdId) {
              const numId = Number(createdId);
              camp.curtidaID = numId;
              if (camp.curtidas) {
                camp.curtidas.curtidasId = numId;
              }
              this.curtidaService.recordLike(id, numId);
            }
          },
          error: (err: any) => {
            if (err?.status === 409 || err?.error?.retorno?.codigo === 409) {
              this.curtidaService
                .loadUserLikes(this.userID!)
                .subscribe((likes) => {
                  const lId = likes.get(id);
                  if (lId) {
                    camp.curtidaID = lId;
                    if (camp.curtidas) camp.curtidas.curtidasId = lId;
                    this.curtidaService.recordLike(id, lId);
                  }
                });
            } else {
              this.likedSet.delete(id);
              camp.totalCurtidas = Math.max(0, (camp.totalCurtidas || 1) - 1);
              if (camp.curtidas) {
                camp.curtidas.totalCurtidas = camp.totalCurtidas;
                camp.curtidas.utilizadorCurtiu = false;
              }
              this.showToast('Não foi possível registar o gosto.');
            }
          },
        });
      this.showToast('Adicionado aos gostos!');
    }
  }

  toggleSave(camp: any) {
    const id = Number(camp.id);
    if (this.savedSet.has(id)) {
      this.savedSet.delete(id);
      this.showToast('Campanha removida dos favoritos');
    } else {
      this.savedSet.add(id);
      this.showToast('Campanha guardada com sucesso!');
    }
    try {
      localStorage.setItem(
        'fundcube_saved_campaigns',
        JSON.stringify(Array.from(this.savedSet)),
      );
    } catch (e) {
      console.error(e);
    }
  }

  shareCard(camp: any) {
    const url = `${window.location.origin}/detalhe-campanha/${camp.id}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        this.showToast('Link copiado para a área de transferência!');
      });
    } else {
      this.showToast(url);
    }
  }

  subscribeNewsletter() {
    if (!this.newsletterEmail || !this.newsletterEmail.includes('@')) {
      this.showToast('Por favor insira um email válido.');
      return;
    }
    this.showToast('Subscrito com sucesso na newsletter!');
    this.newsletterEmail = '';
  }

  openCommentModal(camp: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.userID) {
      this.showToast('Faça login para comentar campanhas.');
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    this.activeCommentCamp = camp;
    this.commentContent = '';
    this.showCommentModal = true;
    setTimeout(() => {
      this.commentInputRef?.nativeElement?.focus();
    }, 100);
  }

  closeCommentModal(): void {
    this.showCommentModal = false;
    this.activeCommentCamp = null;
    this.commentContent = '';
    this.isSubmittingComment = false;
  }

  onCommentKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSubmittingComment && this.commentContent.trim()) {
        this.submitComment();
      }
    }
  }

  submitComment(): void {
    const conteudo = this.commentContent.trim();
    if (!conteudo || this.isSubmittingComment || !this.activeCommentCamp) {
      return;
    }

    this.isSubmittingComment = true;
    const camp = this.activeCommentCamp;
    const params = {
      Id: 0,
      utilizadorID: this.userID,
      campanhaID: camp.id,
      conteudo: conteudo,
    };

    this.comentarioService.store(params).subscribe({
      next: (res: any) => {
        this.isSubmittingComment = false;
        camp.totalComentarios = (camp.totalComentarios || 0) + 1;
        this.closeCommentModal();
        this.showToast('Comentário publicado com sucesso!');
        this.campanhaService.show(camp.id).subscribe({
          next: (freshRes: any) => {
            if (freshRes?.data) {
              camp.totalComentarios = freshRes.data.totalComentarios;
              if (freshRes.data.comentarios) {
                camp.comentarios = freshRes.data.comentarios;
              }
            }
          },
        });
      },
      error: (err: any) => {
        this.isSubmittingComment = false;
        const msg =
          err?.error?.retorno?.mensagem ||
          'Não foi possível publicar o comentário.';
        this.showToast(msg);
        console.error('Erro ao comentar campanha:', err);
      },
    });
  }

  showToast(msg: string) {
    this.toastMessage = msg;
    this.toastVisible = true;
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => {
      this.toastVisible = false;
    }, 3200);
  }

  cleanDescription(
    text: string | null | undefined,
    limit: number = 180,
  ): string {
    if (!text || typeof text !== 'string') {
      return 'Projecto de angariação de fundos verificado pela FundCube.';
    }

    const clean = text
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();

    if (!clean) {
      return 'Projecto de angariação de fundos verificado pela FundCube.';
    }

    if (limit && clean.length > limit) {
      return clean.substring(0, limit).trim() + '...';
    }

    return clean;
  }
}
